/** @format */

/* eslint react-hooks/rules-of-hooks: "warn", max-lines: "off", @typescript-eslint/prefer-nullish-coalescing: "warn" */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import Downshift, {
    ControllerStateAndHelpers,
    DownshiftProps,
} from 'downshift';
import { useTranslation } from 'next-i18next';
import React, {
    FocusEvent,
    ForwardRefRenderFunction,
    Fragment,
    ReactNode,
    Ref,
    RefObject,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { propValidator } from '@toucan/helpers/emotion/prop-validator';
import { MediaQuery } from '@toucan/helpers/media-queries';

import CloseIcon from '@toucan/icons/legacy/cross.svg';
import SearchSVG from '@toucan/icons/search.svg';

import {
    ApplicationContext,
    ScrollLock,
} from '@toucan/client/global/application';
import {
    IconButton,
    IconButtonFill,
} from '@toucan/client/shared/components/icon-button';
import { Input, InputSize } from '@toucan/client/shared/components/input';
import { Link } from '@toucan/client/shared/components/link';
import { useKeyPress } from '@toucan/client/shared/hooks/use-key-press';
import { useMediaQuery } from '@toucan/client/shared/hooks/use-media-query';

import { styles } from './typeahead.styles';

export enum TypeaheadVariant {
    SEARCH_BAR = 'search-bar',
    INLINE = 'inline',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TypeaheadSuggestion {
    /**
     * The search term
     */
    term: string;

    /**
     * Optional meta for the typeahead suggestion
     */
    meta?: Record<string, any>;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TypeaheadProps {
    /**
     * The variant of the Typeahead component
     *
     * 1. SEARCH_BAR
     * 2. INLINE
     *
     * @default INLINE
     */
    variant?: TypeaheadVariant;

    /**
     * Child elements to show under the search suggestions
     */
    children?: ReactNode;

    /**
     * Suggestions to show in the component
     */
    suggestions: TypeaheadSuggestion[];

    /**
     * The search value
     */
    searchValue: string;

    /**
     * Label for the input
     */
    label: string;

    /**
     * Label for the clear button
     */
    clearButtonLabel?: string;

    /**
     * Label for the searching the suggestions
     */
    searchLabel?: string;

    /**
     * If the border underneath the input should be shown
     *
     * @default true
     */
    showBorder?: boolean;

    /**
     * The ref to pass to the input element
     */
    inputRef?: RefObject<HTMLInputElement>;

    /**
     * The text used for when no suggestions are available
     */
    noSuggestionsText?: string;

    /**
     * The text to show above the search suggestions
     */
    suggestionsText?: string;

    /**
     * If new suggestions are loading
     */
    isLoading: boolean;

    /**
     * The title that shows at the top of the suggestions
     * when viewed at a mobile viewport
     */
    mobileSuggestionsTitle?: string;

    /**
     * Name attribute of the Input component
     */
    name?: string;

    /**
     * If the input should be readonly
     */
    readOnly?: boolean;

    /**
     * The autoComplete prop for the Input component
     */
    autoComplete?: string;

    /**
     * Whether the Input component should be focussed.
     *
     * @default false
     */
    autoFocus?: boolean;

    /**
     * Handler called after the presses enter on the input
     */
    onSearch?: (searchTerm: string) => void;

    /**
     * Handler called after the user clicks `Search for...` label
     */
    onSearchLabelClick?: (searchTerm: string) => void;

    /**
     * Function called after the onClear handler from Input component is called
     */
    onClear?: () => void;

    /**
     * Function called when the input blurs
     */
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;

    /**
     * Function called when the input is focused
     */
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;

    /**
     * The error message to show when the input is invalid.
     */
    error?: string;

    /**
     * Whether the input has been touched. Used by validation to update error messages
     */
    touched?: boolean;

    /**
     * Event handler that fires when the internal state changes
     */
    onStateChange?: DownshiftProps<TypeaheadSuggestion>['onStateChange'];

    /**
     * Event handler that fires when the input value changes. This needs to be set if you want
     * to avoid issues with cursor positions.
     * @see https://github.com/downshift-js/downshift#oninputvaluechange
     */
    onInputValueChange?: DownshiftProps<TypeaheadSuggestion>['onInputValueChange'];

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * Width to set the search suggestions container
     */
    suggestionsWidth?: string;

    /**
     * Optional render override for a term, called instead of rendering
     * the suggestion.term value
     * @param arg
     */
    renderOverride?: (arg: TypeaheadSuggestion) => string | ReactNode;

    /**
     * Optional key override used in place of suggestion.term when the term
     * list is rendered
     * @param arg
     */
    keyOverride?: (arg: TypeaheadSuggestion) => string;

    /**
     * Handle Typeahead item suggestion
     * @param suggestion
     */
    onChange?: (
        suggestion: TypeaheadSuggestion | null,
        stateAndHelpers: ControllerStateAndHelpers<TypeaheadSuggestion>
    ) => void;

    /**
     * An optional input size
     *
     * @default InputSize.MEDIUM
     */
    inputSize?: InputSize;

    /**
     * An icon that appears at the end of the typeahead
     */
    suffixIcon?: ReactNode;
    placeholder?: string;
    helpText?: string;
}

export type StyledTypeaheadProps = TypeaheadProps & {
    isOpen: boolean;
    highlightedIndex: number;
};

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface TypeaheadHandles {
    /**
     * Function that will focus the input
     */
    focus: () => void;

    /**
     * Function that when called will close the Typeahead suggestions
     */
    close: () => void;
}

export type OmitOnChange = Omit<StyledTypeaheadProps, 'onChange'>;

const StyledTypeahead = styled('div').withConfig({
    shouldForwardProp: (prop) =>
        propValidator(['isLoading', 'onChange', 'aria-labelledby'])(prop),
})<OmitOnChange>`
    ${styles}
`;

const SCROLL_LOCK_SELECTORS = [
    '.typeahead-items',
    '.typeahead-items *',
    '.typeahead__no-suggestions-container',
    '.typeahead__no-suggestions-container *',
];

const TypeaheadWithRef: ForwardRefRenderFunction<
    TypeaheadHandles,
    TypeaheadProps
> = (
    {
        label,
        onStateChange,
        onInputValueChange,
        onSearch,
        onSearchLabelClick,
        searchValue,
        suggestions,
        suggestionsText,
        clearButtonLabel,
        noSuggestionsText,
        onClear,
        suggestionsWidth,
        children,
        onBlur,
        error,
        touched,
        onFocus,
        isLoading,
        readOnly,
        keyOverride,
        renderOverride,
        autoComplete,
        mobileSuggestionsTitle,
        className = '',
        autoFocus = false,
        name = 'typeahead-search',
        showBorder = true,
        inputRef = useRef<HTMLInputElement>(null),
        variant = TypeaheadVariant.INLINE,
        onChange,
        inputSize = InputSize.MEDIUM,
        suffixIcon,
        searchLabel,
        placeholder,
        helpText,
        ...otherProps
    }: TypeaheadProps,
    ref: Ref<TypeaheadHandles>
) => {
    const { addPageScrollLock, removePageScrollLock } =
        useContext(ApplicationContext);
    const { t } = useTranslation();
    const rootRef = useRef(null);
    const searchButtonRef = useRef<HTMLButtonElement>(null);

    const [inputFocused, setInputFocused] = useState<boolean>(false);
    const [mouseHovered, setMouseHovered] = useState<boolean>(false);

    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(
        null
    );

    const iosFixSkipBlurRef = useRef<boolean>(false);
    const iosFixScrollPosition = useRef<number>(-1);

    const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(
        () =>
            typeof onSearch === 'function' ||
            typeof onSearchLabelClick === 'function'
    );

    /**
     * If the mobile suggestions are showing.
     * Only for the INLINE variant
     */
    const [mobileOpen, setMobileOpen] = useState(false);

    const isMobile = useMediaQuery(MediaQuery.MOBILE);

    const isOpen =
        variant === TypeaheadVariant.INLINE && isMobile
            ? mobileOpen
            : (searchValue.trim() !== '' ||
                  variant === TypeaheadVariant.SEARCH_BAR) &&
              (inputFocused || mouseHovered);

    const suggestionsWidthValue =
        variant === TypeaheadVariant.INLINE && isMobile
            ? 'auto'
            : suggestionsWidth;

    const showNoSuggestionsText =
        suggestions.length === 0 &&
        (searchValue.trim() !== '' ||
            variant === TypeaheadVariant.SEARCH_BAR) &&
        !isLoading;

    /**
     * Handle input focus for iOS devices.
     * Don’t focus the input until the window is scrolled to the top
     *
     * @param e The focus event from the input
     */
    function iosScrollFixOnFocus(e: FocusEvent<HTMLInputElement>) {
        if (isMobile) {
            addPageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
            if (!mobileOpen) {
                iosFixSkipBlurRef.current = true;
                iosFixScrollPosition.current = window.pageYOffset;

                // Blur the input so ios doesn't try scrolling to it
                e.target.blur();
            }
        }
    }

    /**
     * Handle input blur for iOS devices.
     * Don’t focus the input until the window is scrolled to the top
     *
     * @param e The blur event from the input
     * @returns {boolean} Whether the event should continue to bubble
     */
    function iosScrollFixOnBlur(e: FocusEvent<HTMLInputElement>): boolean {
        const { target } = e;
        if (iosFixSkipBlurRef.current) {
            iosFixSkipBlurRef.current = false;

            // Scroll to top of page so keyboard doesn't bug out when input is focuded
            window.scrollTo(0, 0);

            /**
             * setTimeout is required for ios otherwise a maximum call stack
             * size errors happens
             */
            setTimeout(() => {
                target.setAttribute('autofocus', 'autofocus');
                target.focus();
            }, 0);

            return false;
        }

        removePageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
        return true;
    }

    /**
     * Resets any state when the typeahead closes
     * Helps to fix ios issues with the keyboard
     */
    function iosScrollFixReset() {
        if (isMobile && iosFixScrollPosition.current >= 0) {
            // Scroll back to previous position
            window.scrollTo(0, iosFixScrollPosition.current);
        }
        iosFixScrollPosition.current = -1;
        iosFixSkipBlurRef.current = false;
        if (inputRef.current) {
            inputRef.current.removeAttribute('autofocus');
        }
    }

    /**
     * Handles the closing of the modal when on mobile
     */
    function onCloseHandler() {
        setMobileOpen(false);
        removePageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
        iosScrollFixReset();
    }

    /**
     * Tried using setState from child function for setting inputValue but didn't work properly.
     * Hence adding the type assertion to the onStateChange function
     */
    function onClearHandler() {
        onStateChange &&
            (onStateChange as any)({
                type: Downshift.stateChangeTypes.changeInput,
                inputValue: '',
            });

        onClear && onClear();
    }

    /**
     * Handles setting internal state when the mouse hovers over the
     * typeahead items
     */
    function handleMouseEnter() {
        setMouseHovered(true);
    }

    /**
     * Handles setting internal state when the mouse leaves the
     * typeahead items
     */
    function handleMouseLeave() {
        setMouseHovered(false);
    }

    /**
     * Handles calling onInputValueChange without any undefined values
     */
    function handleInputChange(
        value: string | undefined,
        helpers: ControllerStateAndHelpers<TypeaheadSuggestion>
    ) {
        typeof value === 'string' &&
            onInputValueChange &&
            onInputValueChange(value, helpers);
    }

    /**
     * Closes the typeahead suggestions
     */
    function close() {
        inputRef.current && inputRef.current.blur();
        setInputFocused(false);
        setMouseHovered(false);
        setMobileOpen(false);
        removePageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
        iosScrollFixReset();
    }

    /**
     * Focuses the input
     */
    function focus() {
        inputRef.current && inputRef.current.focus();
        setInputFocused(true);
        setMouseHovered(true);
    }

    const childrenContainer = children ? (
        <div className="typeahead-children">{children}</div>
    ) : null;

    const searchButton =
        onSearchLabelClick && showAdvancedSearch ? (
            <Link
                className="typeahead-search"
                data-testid="typeahead__search-button"
                data-isactive={highlightedIndex === 0}
                ref={searchButtonRef}
                onClick={() => {
                    onSearchLabelClick(searchValue);

                    // hide the button as no need to search for same result multiple times
                    setShowAdvancedSearch(false);
                }}
            >
                {searchLabel ?? t('Search for')}: {searchValue}
            </Link>
        ) : null;

    /**
     * Returns the text to be displayed as the title for the typeahead results pane
     *
     * @returns {ReactNode}
     */
    function getTypeaheadItemsText() {
        const text =
            (onSearch || onSearchLabelClick) &&
            !showAdvancedSearch &&
            !!searchValue
                ? t('Search results for "{{searchValue}}"', { searchValue })
                : suggestionsText;

        if (!text) {
            return null;
        }

        return (
            <p
                className="suggestions-text"
                data-testid="typeahead__suggestions-text"
            >
                {text}
            </p>
        );
    }

    const noSuggestions =
        noSuggestionsText !== null && showNoSuggestionsText ? (
            <Fragment>
                {searchButton}
                <p
                    data-testid="typeahead__no-suggestions"
                    className="no-suggestions"
                >
                    {noSuggestionsText}
                </p>
                {childrenContainer}
            </Fragment>
        ) : null;

    const inputIcon =
        variant === TypeaheadVariant.INLINE ? (
            <Icon variant={IconVariant.DECORATIVE} size={IconSize.S}>
                <SearchSVG />
            </Icon>
        ) : null;

    // If the search value has been changed let the 'search for...' label be shown
    useEffect(() => {
        onSearchLabelClick && setShowAdvancedSearch(true);
        setHighlightedIndex(null);
    }, [searchValue]);

    useEffect(() => {
        if (!isMobile) {
            removePageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
            iosScrollFixReset();
        }

        return () => {
            removePageScrollLock(ScrollLock.TYPEAHEAD, SCROLL_LOCK_SELECTORS);
        };
    }, [isMobile]);

    // Close the suggestions after the user presses escape with the input focused
    useKeyPress(inputRef.current, ['Escape', 'Esc'], () => {
        if (variant !== TypeaheadVariant.INLINE) {
            return;
        }
        onClearHandler();
        close();
    });

    /**
     * Adds a close method to the ref of this component.
     * @see https://reactjs.org/docs/hooks-reference.html#useimperativehandle
     */
    useImperativeHandle(ref, () => {
        return {
            close,
            focus,
        };
    });

    /**
     * Handler for when the enter key is pressed inside the component
     *
     * @returns {void}
     */
    function handleEnterPressed(event: KeyboardEvent) {
        event.preventDefault();

        if (!showAdvancedSearch && highlightedIndex === null) {
            return;
        }

        if (showAdvancedSearch) {
            // If the search button is highlighted fire the search click event
            if (onSearch) {
                onSearch(searchValue);
            }

            // hide the button as no need to search for same result multiple times
            setShowAdvancedSearch(false);
        }
    }

    /**
     * Handler that is called when the up key is pressed
     *
     * @param {KeyboardEvent} event - The keyboard event
     * @return {void}
     */
    function handleUpPressed(event: KeyboardEvent) {
        event.preventDefault();

        if (highlightedIndex === 0 || highlightedIndex === null) {
            // We are at the top of the suggestions list
            showAdvancedSearch
                ? setHighlightedIndex(suggestions.length)
                : setHighlightedIndex(suggestions.length - 1);
        } else {
            setHighlightedIndex(highlightedIndex - 1);
        }
    }

    /**
     * Handler that is called when the down key is pressed
     *
     * @param {KeyboardEvent} event - The keyboard event
     * @return {void}
     */
    function handleDownPressed(event: KeyboardEvent) {
        event.preventDefault();

        if (
            typeof highlightedIndex === 'number' &&
            (showAdvancedSearch
                ? highlightedIndex - 1 < suggestions.length - 1
                : highlightedIndex < suggestions.length - 1)
        ) {
            setHighlightedIndex(highlightedIndex + 1);
        } else {
            setHighlightedIndex(0);
        }
    }

    function onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            handleEnterPressed(event);
        } else if (['Up', 'ArrowUp'].includes(event.key)) {
            handleUpPressed(event);
        } else if (['Down', 'ArrowDown'].includes(event.key)) {
            handleDownPressed(event);
        }
    }

    return (
        <Downshift
            ref={rootRef}
            itemToString={(suggestion) => suggestion?.term || ''}
            onStateChange={onStateChange}
            onInputValueChange={handleInputChange}
            onChange={onChange}
            inputValue={searchValue}
            inputId={name}
            labelId={name}
            isOpen={isOpen}
            highlightedIndex={highlightedIndex}
        >
            {({
                getRootProps,
                getInputProps,
                getItemProps,
                getLabelProps,
                getMenuProps,
                highlightedIndex,
            }) => {
                return (
                    <StyledTypeahead
                        suggestions={suggestions}
                        searchValue={searchValue}
                        showBorder={showBorder}
                        variant={variant}
                        isOpen={isOpen}
                        isLoading={isLoading}
                        highlightedIndex={highlightedIndex}
                        className={'typeahead ' + className}
                        {...otherProps}
                        {...getRootProps({ refKey: 'ref' })}
                    >
                        {mobileSuggestionsTitle && (
                            <p className="suggestions-title">
                                {mobileSuggestionsTitle}

                                <IconButton
                                    label={t('Close')}
                                    border={false}
                                    fill={IconButtonFill.HOLLOW}
                                    onClick={onCloseHandler}
                                    data-testid="suggestion-close-button"
                                    className="suggestion-close"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </p>
                        )}
                        <Input
                            data-testid="typeahead__input"
                            autoFocus={autoFocus}
                            label={label}
                            clearButtonLabel={clearButtonLabel}
                            onClear={onClearHandler}
                            labelProps={getLabelProps()}
                            alwaysShowClearButton={true}
                            prefixIcon={inputIcon}
                            suffixIcon={suffixIcon}
                            error={error}
                            touched={touched}
                            autoComplete={autoComplete}
                            readOnly={readOnly}
                            inputSize={inputSize}
                            placeholder={placeholder}
                            helpText={helpText}
                            {...getInputProps({
                                name,
                                type: 'search',
                                value: searchValue,
                                className: 'input',
                                ref: inputRef,
                                onFocus: (e: FocusEvent<HTMLInputElement>) => {
                                    if (readOnly) {
                                        return;
                                    }

                                    setInputFocused(true);
                                    setMobileOpen(true);
                                    iosScrollFixOnFocus(e);
                                    onFocus && onFocus(e);
                                },
                                onKeyDown,
                            })}
                            /**
                             * Overwriting the downshift blur handler as it will reset
                             * the input state when blurred (on mobile). Which is not the behaviour we want
                             *
                             * @see https://github.com/downshift-js/downshift/blob/master/src/downshift.js#L861
                             */
                            onBlur={(e: FocusEvent<HTMLInputElement>) => {
                                const continueHandler = iosScrollFixOnBlur(e);
                                if (continueHandler) {
                                    setInputFocused(false);
                                    onBlur && onBlur(e);
                                }
                            }}
                        />
                        {isOpen && searchValue && suggestions.length === 0 && (
                            <div
                                className="typeahead__no-suggestions-container"
                                style={{ width: suggestionsWidthValue }}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                {noSuggestions}
                            </div>
                        )}
                        {isOpen && suggestions.length !== 0 && (
                            <div
                                className="typeahead-items"
                                data-testid="typeahead__items"
                                style={{ width: suggestionsWidthValue }}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                {searchButton}
                                {getTypeaheadItemsText()}
                                <ul
                                    {...getMenuProps({
                                        className: 'suggestions',
                                    })}
                                >
                                    {suggestions.map((suggestion, index) => {
                                        const itemIndex = showAdvancedSearch
                                            ? index + 1
                                            : index;
                                        return (
                                            <li
                                                {...getItemProps({
                                                    item: suggestion,
                                                    className: 'suggestion',
                                                    index: itemIndex,
                                                })}
                                                key={
                                                    keyOverride
                                                        ? keyOverride(
                                                              suggestion
                                                          )
                                                        : `${suggestion.term}-${index}`
                                                }
                                                data-selected={
                                                    searchValue.toLowerCase() ===
                                                    suggestion.term.toLowerCase()
                                                }
                                                data-isactive={
                                                    highlightedIndex ===
                                                    itemIndex
                                                }
                                                data-testid="typeahead-suggestion"
                                            >
                                                {renderOverride
                                                    ? renderOverride(suggestion)
                                                    : suggestion.term}
                                            </li>
                                        );
                                    })}
                                </ul>
                                {childrenContainer}
                            </div>
                        )}
                    </StyledTypeahead>
                );
            }}
        </Downshift>
    );
};

export const Typeahead = forwardRef(TypeaheadWithRef);
